import { useState } from 'react';
import { 
  Box, 
  Typography, 
  Snackbar, 
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import SurveyProgressBar from '../components/survey/SurveyProgressBar';
import ManualQuestionForm from '../components/survey/ManualQuestionForm';
import QuestionList from '../components/survey/QuestionList';
import SurveyPreviewPanel from '../components/survey/SurveyPreviewPanel';
import { getFullUserData, getUserBusinessName } from '../utils/userHelper';
import { SurveyQuestion, QuestionFormData, CreationMethod } from '../types/survey';
import { createSurveyFromForm } from '../services/surveyService';

const PageContainer = styled(Box)({
  display: 'flex',
  minHeight: '100vh',
  bgcolor: '#F8F9FA'
});

const MainContent = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column'
});

const ContentArea = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4, 6),
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(4),
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: '2fr 1fr'
  }
}));

const LeftColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3)
}));

const ManualSurveyBuilder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = getFullUserData();
  const businessName = getUserBusinessName();
  const campaignData = location.state?.campaignData;
  const campaignId = location.state?.campaignId || campaignData?.id;

  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<SurveyQuestion | null>(null);
  const [editForm, setEditForm] = useState<{
    question: string;
    answerType: string;
    isMandatory: boolean;
  }>({
    question: '',
    answerType: 'text',
    isMandatory: false
  });

  const handleAddQuestion = (questionData: QuestionFormData) => {
    const newQuestion: SurveyQuestion = {
      id: `question-${Date.now()}`,
      question: questionData.question,
      answerType: questionData.answerType,
      isMandatory: questionData.isMandatory,
      placeholder: questionData.answerType === 'number' ? 'Enter a number from 1-10' : undefined
    };

    console.log('Adding question with mandatory:', questionData.isMandatory, 'Type:', typeof questionData.isMandatory);
    setQuestions([...questions, newQuestion]);
  };

  const handleEditQuestion = (question: SurveyQuestion) => {
    setEditingQuestion(question);
    setEditForm({
      question: question.question || '',
      answerType: question.answerType || 'text',
      isMandatory: question.isMandatory || false
    });
  };

  const handleSaveEdit = () => {
    if (!editingQuestion || !editForm.question.trim()) {
      return;
    }

    const updatedQuestions = questions.map(q => 
      q.id === editingQuestion.id
        ? {
            ...q,
            question: editForm.question.trim(),
            answerType: editForm.answerType,
            isMandatory: editForm.isMandatory
          }
        : q
    );

    setQuestions(updatedQuestions);
    setEditingQuestion(null);
    setEditForm({
      question: '',
      answerType: 'text',
      isMandatory: false
    });
  };

  const handleCancelEdit = () => {
    setEditingQuestion(null);
    setEditForm({
      question: '',
      answerType: 'text',
      isMandatory: false
    });
  };

  const handleDeleteQuestion = (questionId: string) => {
    setQuestions(questions.filter(q => q.id !== questionId));
  };

  const handleReorderQuestions = (reorderedQuestions: SurveyQuestion[]) => {
    setQuestions(reorderedQuestions);
  };

  const handleSaveAndContinue = async () => {
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      // Validation
      if (!campaignId) {
        throw new Error('Campaign ID is required. Please create a campaign first.');
      }

      if (questions.length === 0) {
        throw new Error('Please add at least one question to the survey');
      }

      // Convert UI questions to API format
      const apiQuestions = questions.map((q, index) => {
        const converted = {
          question_text: q.question || '',
          answer_type: q.answerType || 'text',
          is_mandatory: Boolean(q.isMandatory), // Explicitly convert to boolean
          question_order: index + 1, // Order based on position in array (1-indexed)
          ...(q.options && q.options.length > 0 ? { options: q.options } : {})
        };
        console.log(`Question ${index + 1}: isMandatory=${q.isMandatory}, is_mandatory=${converted.is_mandatory}`);
        return converted;
      });

      console.log('API Questions:', JSON.stringify(apiQuestions, null, 2));

      // Create survey via API
      const response = await createSurveyFromForm({
        campaignId: campaignId,
        surveyName: campaignData?.campaign_name || 'Manual Survey',
        description: 'Survey created manually with custom questions',
        creationMethod: CreationMethod.MANUAL,
        questions: apiQuestions as any,
      });

      console.log('Survey created successfully:', response.data?.survey);
      setSuccess('Survey created successfully! Redirecting...');

      // Navigate to customer upload step
      navigate('/campaigns/create/customers', {
        state: { 
          campaignData, 
          surveyData: response.data?.survey,
          campaignId 
        }
      });

    } catch (err) {
      console.error('Survey creation error:', err);
      setError(err instanceof Error ? err.message : 'Failed to create survey. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageContainer>
      <Sidebar user={userData} />
      
      <MainContent>
        <DashboardHeader
          businessName={businessName}
          userAvatar={userData.avatar}
          businessLogo={userData.businessLogo}
        />
        
        <ContentArea>
          {/* Left Column - Main Content */}
          <LeftColumn>
            {/* Page Heading */}
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 900,
                  fontSize: '2.5rem',
                  letterSpacing: '-0.033em',
                  mb: 1,
                  color: 'text.primary'
                }}
              >
                Create New Survey
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: '1rem',
                  color: 'text.secondary'
                }}
              >
                Add questions manually or use AI to generate them from a product URL.
              </Typography>
            </Box>

            {/* Progress Bar */}
            <SurveyProgressBar
              currentStep={2}
              totalSteps={3}
              stepLabel="Add Questions"
            />

            {/* Manual Question Form */}
            <ManualQuestionForm onAddQuestion={handleAddQuestion} />

            {/* Question List */}
            {questions.length > 0 && (
              <QuestionList
                questions={questions}
                onEditQuestion={handleEditQuestion}
                onDeleteQuestion={handleDeleteQuestion}
                onReorderQuestions={handleReorderQuestions}
              />
            )}
          </LeftColumn>

          {/* Right Column - Survey Preview */}
          <Box>
            <SurveyPreviewPanel
              questions={questions}
              onSaveAndContinue={handleSaveAndContinue}
              disabled={questions.length === 0 || isLoading}
            />
          </Box>
        </ContentArea>
      </MainContent>

      {/* Success/Error Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      {/* Edit Question Dialog */}
      <Dialog 
        open={!!editingQuestion} 
        onClose={handleCancelEdit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: '"Inter", sans-serif', fontWeight: 700, fontSize: '1.125rem' }}>
          Edit Question
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
            <TextField
              label="Question Text"
              fullWidth
              multiline
              rows={3}
              value={editForm.question}
              onChange={(e) => setEditForm({ ...editForm, question: e.target.value })}
              placeholder="Enter your question here..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.875rem'
                },
                '& .MuiInputLabel-root': {
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.875rem'
                }
              }}
            />

            <FormControl fullWidth>
              <InputLabel sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem' }}>
                Answer Type
              </InputLabel>
              <Select
                value={editForm.answerType}
                label="Answer Type"
                onChange={(e) => setEditForm({ ...editForm, answerType: e.target.value })}
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.875rem'
                }}
              >
                <MenuItem value="text" sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem' }}>Text</MenuItem>
                <MenuItem value="rating" sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem' }}>Rating (1-5)</MenuItem>
                <MenuItem value="number" sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem' }}>Number</MenuItem>
                <MenuItem value="yesno" sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem' }}>Yes/No</MenuItem>
                <MenuItem value="multiple_choice" sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem' }}>Multiple Choice</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={editForm.isMandatory}
                  onChange={(e) => setEditForm({ ...editForm, isMandatory: e.target.checked })}
                />
              }
              label="Make this question mandatory"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.875rem'
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button 
            onClick={handleCancelEdit}
            sx={{
              fontFamily: '"Inter", sans-serif',
              color: '#6B7280',
              fontWeight: 600,
              fontSize: '0.875rem',
              textTransform: 'none',
              px: 2,
              '&:hover': {
                backgroundColor: '#F3F4F6'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained"
            disabled={!editForm.question.trim()}
            sx={{
              fontFamily: '"Inter", sans-serif',
              backgroundColor: '#4F46E5',
              fontWeight: 600,
              fontSize: '0.875rem',
              textTransform: 'none',
              px: 2,
              borderRadius: '8px',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#4338CA',
                boxShadow: 'none'
              },
              '&.Mui-disabled': {
                backgroundColor: '#E5E7EB',
                color: '#9CA3AF'
              }
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default ManualSurveyBuilder;

